/**
 * The driver's live state for every screen: their shift, current job, queue and any offer.
 * Kept fresh over the realtime connection (and by polling, in case that drops), with the
 * location shared and the screen kept on while they're on shift.
 */
import { get, localTime, post } from "@taxi/mobile-web/api";
import { useInterval } from "@taxi/mobile-web/hooks";
import { useRealtime } from "@taxi/mobile-web/realtime";
import { useToast } from "@taxi/mobile-web/ui";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { unlockSound, useWakeLock } from "./device";
import { type Sharing, useLocationSharing } from "./location";
import { useMe, useSession } from "./session";
import type { DriverJobs, DriverMe, DriverOffer } from "./types";

export type ShiftAction = "clock-on" | "clock-off" | "pause" | "resume";

type Driver = {
  me: DriverMe;
  jobs: DriverJobs | null;
  refresh: () => Promise<void>;
  shift: (action: ShiftAction) => Promise<void>;
  location: Sharing & { retry: () => void };
  /** The realtime connection is up. */
  live: boolean;
  /** Goes up whenever the job board changes. */
  boardVersion: number;
  /** Takes an offer off screen straight away (answered, or out of time). */
  dismissOffer: (offerId: string) => void;
};

const DriverContext = createContext<Driver | null>(null);

export function useDriver(): Driver {
  const driver = useContext(DriverContext);
  if (!driver) throw new Error("useDriver outside DriverProvider");
  return driver;
}

const WITHDRAWN: Record<string, string> = {
  expired: "The offer ran out of time",
  cancelled: "That job was cancelled",
  taken: "That job has gone to another driver",
};

export function DriverProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const { refreshMe } = useSession();
  const toast = useToast();
  const [jobs, setJobs] = useState<DriverJobs | null>(null);
  const latestJobs = useRef(jobs);
  useLayoutEffect(() => {
    latestJobs.current = jobs;
  });
  // Offers already answered, or run out here: not shown again if the API hasn't caught up.
  const dismissed = useRef(new Set<string>());
  const showable = useCallback(
    (offer: DriverOffer | null) =>
      offer && !dismissed.current.has(offer.id) && localTime(offer.expiresAt) > Date.now()
        ? offer
        : null,
    [],
  );
  const [boardVersion, setBoardVersion] = useState(0);
  const onShift = me.live.status !== "off";

  const refreshJobs = useCallback(async () => {
    try {
      const fresh = await get<DriverJobs>("/driver/jobs");
      setJobs({ ...fresh, offer: showable(fresh.offer) });
    } catch {
      // Tried again soon.
    }
  }, [showable]);

  const refresh = useCallback(async () => {
    await Promise.all([refreshJobs(), refreshMe().catch(() => undefined)]);
  }, [refreshJobs, refreshMe]);

  useEffect(() => {
    void refreshJobs();
  }, [refreshJobs]);

  const { connected } = useRealtime((event) => {
    switch (event.type) {
      case "offer.created": {
        const offer = showable(event.offer);
        if (offer) setJobs((current) => (current ? { ...current, offer } : current));
        void refreshMe().catch(() => undefined);
        return;
      }
      case "offer.withdrawn":
        if (latestJobs.current?.offer?.id === event.offerId) {
          toast(WITHDRAWN[event.reason] ?? "The offer has gone");
          setJobs((current) => (current ? { ...current, offer: null } : current));
        }
        void refresh();
        return;
      case "job.created":
      case "job.updated":
      case "reconnected":
        void refresh();
        return;
      case "board.updated":
        setBoardVersion((n) => n + 1);
        return;
    }
  });

  // In case a realtime message goes missing: often while on shift, now and then off it.
  useInterval(() => void refresh(), onShift ? 20_000 : 120_000);

  // A push arrived (e.g. an offer while the realtime connection was down): look now.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === "push") void refresh();
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [refresh]);

  // Back on screen: catch up.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const location = useLocationSharing(onShift);
  useWakeLock(onShift);

  const shift = useCallback(
    async (action: ShiftAction) => {
      unlockSound();
      await post(`/driver/shift/${action}`);
      await refresh();
    },
    [refresh],
  );

  const dismissOffer = useCallback((offerId: string) => {
    dismissed.current.add(offerId);
    setJobs((current) => (current?.offer?.id === offerId ? { ...current, offer: null } : current));
  }, []);

  return (
    <DriverContext.Provider
      value={{
        me,
        jobs,
        refresh,
        shift,
        location,
        live: connected,
        boardVersion,
        dismissOffer,
      }}
    >
      {children}
    </DriverContext.Provider>
  );
}
